import { FunctionComponent, MouseEvent, useEffect, useRef, useState } from 'react';
import { LoadGoogleMaps } from 'components/maps/LoadGoogleMaps';
import { PatronLocationButton } from 'components/patron-location/PatronLocationButton';
import { PatronLocationDesktopSearchResultsButton } from './PatronLocationDesktopSearchResultsButton';
import { PatronLocationDialog } from 'components/patron-location/PatronLocationDialog';
import { PatronLocationFinder } from 'components/patron-location/PatronLocationFinder';
import { PatronLocationGhostButton } from 'components/patron-location/PatronLocationGhostButton';
import { PatronLocationTubeSearchInput } from 'components/patron-location/PatronLocationTubeSearchInput';
import { core, payloadToJSON } from '@tock/coretypes';
import { placeResultToLocation } from 'utils/locationUtils';

type PatronLocationEntityType = 'address' | 'city' | 'neighborhood';

export type PatronLocationInitialComponent =
  | typeof PatronLocationTubeSearchInput
  | typeof PatronLocationButton
  | typeof PatronLocationGhostButton
  | typeof PatronLocationDesktopSearchResultsButton;

export interface PatronLocationEntity {
  lat: number;
  lng: number;
  metroAreaId: number;
  placeId: string;
  textFull: string;
  textPrimary: string;
  textSecondary?: string;
  type: PatronLocationEntityType;
}

interface PatronLocationProps {
  googleMapsLoaded: boolean;
  location: PatronLocationEntity;
  onLocation(location: PatronLocationEntity): void;
  initialComponent?: PatronLocationInitialComponent;
}

export const PatronLocation: FunctionComponent<PatronLocationProps> = ({
  googleMapsLoaded,
  initialComponent = PatronLocationButton,
  location,
  onLocation,
}) => {
  const [showFinder, setShowFinder] = useState(false);
  const [loadingText, setLoadingText] = useState<string | undefined>();
  const [token, setToken] = useState<google.maps.places.AutocompleteSessionToken>();
  const initialComponentRef = useRef<HTMLElement>();

  useEffect(() => {
    if (googleMapsLoaded && !token) {
      setToken(new google.maps.places.AutocompleteSessionToken());
    }
  }, [googleMapsLoaded, token]);

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    initialComponentRef.current = e.currentTarget;
    setShowFinder(!showFinder);
  };

  const buttonText = location?.textPrimary;

  const handleLocation = (newLocation: PatronLocationEntity) => {
    onLocation(newLocation);
    setShowFinder(false);
  };

  const handleDialogClose = () => {
    setShowFinder(false);
  };

  const InitialComponent = initialComponent;

  return (
    <>
      {loadingText ? (
        <InitialComponent loading={true} />
      ) : (
        <InitialComponent onClick={handleClick} locationText={buttonText} />
      )}
      {location ? (
        <PatronLocationDialog onClose={handleDialogClose} open={showFinder && !loadingText}>
          <LoadGoogleMaps />
          {token && (
            <PatronLocationFinder
              activeLocation={location}
              googleMapsToken={token}
              onLocation={handleLocation}
              setLoadingText={setLoadingText}
            />
          )}
        </PatronLocationDialog>
      ) : null}
    </>
  );
};

export async function geocodeResultToPatronLocation(
  result: google.maps.GeocoderResult,
  findMetro: (latlng: { latitude: number; longitude: number }) => Promise<{ payload: string }>
): Promise<PatronLocationEntity> {
  const lat = result.geometry.location.lat();
  const lng = result.geometry.location.lng();
  const metroRequest = await findMetro({
    latitude: lat,
    longitude: lng,
  });
  const metroAreaId = payloadToJSON<core.MetroArea>(metroRequest.payload).id;
  const response: PatronLocationEntity = {
    lat,
    lng,
    metroAreaId,
    placeId: result.place_id,
    textFull: result.formatted_address,
    textPrimary: '',
    type: 'address',
  };
  const find = (key: string, short = false): string => {
    const property = short ? 'short_name' : 'long_name';
    const item = result.address_components.find((item) => item.types.includes(key));
    return item ? item[property] : '';
  };

  const location = placeResultToLocation(result.address_components, result.place_id);

  if (result.types.includes('neighborhood')) {
    response.textPrimary = `${find('neighborhood')}, ${location.city}`;
    response.textSecondary = `${location.state}, ${location.country}`;
    response.type = 'neighborhood';
  } else if (result.types.includes('locality') && location.city) {
    response.textPrimary = `${location.city}, ${location.state}`;
    response.textSecondary = `${location.country}`;
    response.type = 'city';
  } else if (location.address && location.address.trim()) {
    response.textPrimary = location.address;
    response.textSecondary = `${location.city}, ${location.state}`;
    response.type = 'address';
  } else {
    response.textPrimary = result.formatted_address;
    response.type = 'address';
  }
  return response;
}
